import { DEFAULT_UNIT_ID, DeliveryStatus, StockStatus } from '@synergy/models';
import { createSelector } from 'reselect';
import { defaultLocale } from '@synergy/config';
import { errorsToTouch, nullIfEmpty, nullIfEmptyLocalizedString } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { ProductDetail } from '../models/product-detail';
import { ProductDetailOverviewFormValues } from '../models/product-detail-overview-form-values';
import { ProductDetailRequest } from '../api/models/product-overview-request';

const productDetailOverviewState = (state: AppState) => state.product.detail.overview;

export const productDetailSelector = createSelector(
    productDetailOverviewState,
    detailState => detailState.productDetail
);

/**
 * The defaults the mask fills in for values the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159): compared against raw server
 * values, every one of these defaults would read as something the user typed.
 */
const withDefaults = (productDetail?: ProductDetail | null): ProductDetailOverviewFormValues => {
    const formValues: ProductDetailOverviewFormValues = {
        categoryIds: [],
        deliveryStatus: DeliveryStatus.Available,
        isActive: false,
        isNormArticle: false,
        isPrioritizedInSearch: false,
        isEcoProduct: false,
        preventImportDeletion: false,
        searchTerms: [],
        stockStatus: StockStatus.AlwaysInStock,
        supplier: null,
        brand: '',
        manufacturer: '',
        manufacturerNumber: '',
        number: '',
        globalTradeItemNumber: '',
        orderUnitId: DEFAULT_UNIT_ID,
        name: { [defaultLocale]: '' },
        quantityInterval: 1,
        packingUnits: productDetail?.packingUnits ?? [],
        minimumOrderQuantity: productDetail?.minimumOrderQuantity ?? 1,
        ...productDetail,
    };

    // A copy rather than a push: the spread above hands over the store's own
    // array, and pushing the empty row into it would change the state this
    // selector is supposed to only read — twice over, now that the baseline
    // goes through here as well.
    return 0 === formValues.packingUnits.length ? { ...formValues, packingUnits: [{ id: -1 }] } : formValues;
};

export const productDetailOverviewFormDataSelector = createSelector(
    productDetailOverviewState,
    detailOverviewState => ({
        formValues: withDefaults(detailOverviewState.productDetail),
        errors: detailOverviewState.errors,
        touched: errorsToTouch(detailOverviewState.errors),
    })
);

/** What the mask compares against to decide whether anything is unsaved. */
export const productDetailOverviewBaselineSelector = createSelector(productDetailOverviewState, detailOverviewState =>
    detailOverviewState.baseline ? withDefaults(detailOverviewState.baseline) : undefined
);

export const productDetailOverviewRequestSelector = createSelector(productDetailOverviewState, detailOverviewState => {
    if (!detailOverviewState.productDetail) {
        return null;
    }

    const { id: _id, updatedAt: _updatedAt, supplier, ...data } = detailOverviewState.productDetail;

    const request: ProductDetailRequest = {
        ...data,
        supplierId: supplier.id,
        contentUnitId: data.contentUnitId ?? null,
        contentQuantity: data.contentQuantity ?? null,
        globalTradeItemNumber: nullIfEmpty(data.globalTradeItemNumber),
        longDescription: nullIfEmptyLocalizedString(data.longDescription),
        shortDescription: nullIfEmptyLocalizedString(data.shortDescription),
        minimumOrderQuantity: data.minimumOrderQuantity,
        maximumOrderQuantity: data.maximumOrderQuantity ?? null,
        recommendedOrderQuantity: data.recommendedOrderQuantity ?? null,
        quantityInterval: data.quantityInterval ?? null,
        packingUnits: data.packingUnits
            .filter(packingUnit => packingUnit.unitId !== null && packingUnit.unitId !== undefined)
            .map(packingUnit => ({
                ...packingUnit,
                id: packingUnit.id < 0 ? undefined : packingUnit.id,
                minimumQuantity: +packingUnit.minimumQuantity,
                maximumQuantity: +packingUnit.maximumQuantity || null,
            })),
        manufacturerNumber: nullIfEmpty(data.manufacturerNumber),
    };
    return request;
});
